import { Link } from 'react-router-dom';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, CtaLink } from '@/components/system';

/**
 * "¿Qué es MEC?" — the positioning statement (pure software) and the Mi family.
 * Names per Brand Guidelines: Mi Dashboard · Mi Envío Cuba · Mi Admin · Mi Cargo · Mi Restaurante.
 */
const family = [
  {
    logo: '/logos/apps/dashboard.png',
    name: 'Mi Dashboard',
    role: 'El panel web de tu equipo.',
    text: 'Clientes, paquetes, cobros y aduana — todo tu día en una sola pantalla.',
    to: '/plataforma/mi-dashboard',
  },
  {
    logo: '/logos/apps/mi-envio-cuba.png',
    name: 'Mi Envío Cuba',
    role: 'La app de tus clientes.',
    text: 'Rastreo artículo por artículo, chat con tu agencia y prueba de entrega.',
    to: '/plataforma/mi-envio-cuba',
  },
  {
    logo: '/logos/apps/scanner.png',
    name: 'Mi Admin',
    role: 'La app de almacén y reparto.',
    text: 'Escaneo masivo, entregas con foto, firma y GPS — incluso sin señal.',
    to: '/plataforma/mi-admin',
  },
];

const comingSoon = [
  { logo: '/logos/apps/cargo.png', name: 'Mi Cargo', text: 'Para transitarias.', to: '/plataforma/mi-cargo' },
  { logo: '/logos/apps/restaurant.png', name: 'Mi Restaurante', text: 'Para comercios aliados.', to: '/plataforma/mi-restaurante' },
];

export function WhatIsMec() {
  const ref = useReveal<HTMLElement>();
  return (
    <Section tone="grey" ref={ref}>
      <Container>
        <div className="mx-auto max-w-5xl text-center">
          <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">¿Qué es MEC?</p>
          <Display data-reveal size="lg">
            Somos software.
            <br />
            No enviamos. No vendemos.
            <br />
            No entregamos.
          </Display>
          <Lead data-reveal className="mt-6 mx-auto max-w-3xl">
            Construimos la plataforma con la que operan las agencias de envíos a Cuba y las
            transitarias que las atienden — conectada en tiempo real, segura, en la nube.
          </Lead>
          <p data-reveal className="mt-5 text-sm sm:text-base text-text-tertiary">
            En términos de la industria: gestión de pedidos, rastreo y transporte, almacén ligero y
            última milla con prueba de entrega — como software. Nuestros clientes mueven la carga;
            nosotros, la información.
          </p>
        </div>

        {/* The family */}
        <div className="mt-16 sm:mt-20 grid md:grid-cols-3 gap-4 sm:gap-5">
          {family.map((app) => (
            <div key={app.name} data-reveal className="rounded-[28px] bg-white p-8 sm:p-9 flex flex-col">
              <img src={app.logo} alt={`Logo de ${app.name}`} width={56} height={56} loading="lazy" className="w-14 h-14 rounded-2xl" />
              <h3 className="mt-6 font-heading text-2xl font-semibold tracking-tight">{app.name}</h3>
              <p className="mt-1 text-text-secondary font-medium">{app.role}</p>
              <p className="mt-3 text-text-secondary leading-relaxed">{app.text}</p>
              <div className="mt-6 pt-2 mt-auto">
                <CtaLink to={app.to} className="text-base">Más información</CtaLink>
              </div>
            </div>
          ))}
        </div>

        <div data-reveal className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-x-10 gap-y-3 text-text-tertiary text-sm sm:text-base">
          {comingSoon.map((app) => (
            <Link key={app.name} to={app.to} className="flex items-center gap-3 hover:text-text-primary">
              <img src={app.logo} alt={`Logo de ${app.name}`} width={28} height={28} loading="lazy" className="w-7 h-7 rounded-lg" />
              <span><span className="font-semibold text-text-secondary">{app.name}</span> — {app.text} <span className="text-text-tertiary">En camino.</span></span>
            </Link>
          ))}
        </div>
      </Container>
    </Section>
  );
}
